import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Briefcase, X, Sparkles, Loader2,
  ChevronLeft, ChevronRight, Send, FileText, ClipboardList, Wand2,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../contexts/UserProfileContext';

import { showToast, handleApiError, RateLimitInfo } from '../utils/errorHandler';
import RateLimitNotification from '../components/RateLimitNotification';
import RecruiterLoggedOutGate from '../components/RecruiterLoggedOutGate';
import AtsPushButton from '../components/AtsPushButton';
import { RecruiterCommandCenterBoard } from '../components/Recruiter/hiring-board/RecruiterCommandCenterBoard';
import { RccAiNudgeBanner } from '../components/Recruiter/hiring-board/RccAiNudgeBanner';
import {
  RecruiterGateCard,
  RecruiterInlineEmpty,
  RecruiterLoadingScreen,
  RecruiterPageShell,
} from '../components/Recruiter/RecruiterStates';
import { parseRoleLevel } from '../constants/recruiterFilterOptions';
import { RECRUITER_DEMO_URL } from '../constants/recruiterContact';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface FeedCandidate {
  cv_id: string;
  score: number;
  keywords: string[];
  preview: string;
  cv_language?: string;
  career_status?: string;
  base_locations?: string;
  preferred_locations?: string;
  role_preferences?: string;
  current_role?: string;
  current_level?: string;
  reach_out_status?: string;
  reach_out_id?: string;
  conversation_id?: string;
  explorer_id?: string;
  is_interested?: boolean;
}

interface JobColumn {
  id: string;
  title: string;
  role: string;
  level: string | null;
  allLocations: string[];
  activeLocations: string[];
  customLocations: string[];
  skills: string[];
  languageFilter: string | null;
  candidates: FeedCandidate[];
  loading: boolean;
  expanded: boolean;
  loaded: boolean;
  displayCount: number;
  totalLoaded: number;
}

const HB_STORAGE_KEY = 'cevee_hiring_board_columns';

const FeatureLine: React.FC<{ icon?: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <li className="flex items-start gap-2.5 text-[13px] text-slate-700 leading-snug">
    {icon ? (
      <span className="mt-0.5 inline-flex w-5 h-5 items-center justify-center rounded-full bg-pink-50 text-pink-500 ring-1 ring-pink-100 shrink-0">
        {icon}
      </span>
    ) : null}
    <span>{children}</span>
  </li>
);

interface PersistedColumnState {
  title: string;
  allLocations?: string[];
  activeLocations: string[];
  customLocations: string[];
  skills: string[];
  languageFilter: string | null;
}

const loadPersistedState = (): Record<string, PersistedColumnState> => {
  try {
    const raw = localStorage.getItem(HB_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const persistColumns = (cols: JobColumn[]) => {
  const state: Record<string, PersistedColumnState> = {};
  for (const c of cols) {
    state[c.title] = {
      title: c.title,
      allLocations: c.allLocations,
      activeLocations: c.activeLocations,
      customLocations: c.customLocations,
      skills: c.skills,
      languageFilter: c.languageFilter,
    };
  }
  try { localStorage.setItem(HB_STORAGE_KEY, JSON.stringify(state)); } catch {}
};

const JOB_SAVES_KEY = 'cevee_hb_job_saves';

const loadJobSaves = (): Record<string, string[]> => {
  try { return JSON.parse(localStorage.getItem(JOB_SAVES_KEY) || '{}'); } catch { return {}; }
};

const persistJobSaves = (saves: Record<string, string[]>) => {
  try { localStorage.setItem(JOB_SAVES_KEY, JSON.stringify(saves)); } catch {}
};

const addJobSave = (cvId: string, jobTitle: string) => {
  const saves = loadJobSaves();
  const jobs = saves[cvId] || [];
  if (!jobs.includes(jobTitle)) saves[cvId] = [...jobs, jobTitle];
  persistJobSaves(saves);
};

const removeJobSave = (cvId: string, jobTitle: string) => {
  const saves = loadJobSaves();
  const jobs = (saves[cvId] || []).filter((j) => j !== jobTitle);
  if (jobs.length) saves[cvId] = jobs; else delete saves[cvId];
  persistJobSaves(saves);
};

const getJobSaves = (cvId: string): string[] => {
  return loadJobSaves()[cvId] || [];
};

const cleanupPersistedState = (activeTitles: string[]) => {
  try {
    const raw = localStorage.getItem(HB_STORAGE_KEY);
    if (!raw) return;
    const state = JSON.parse(raw);
    const active = new Set(activeTitles);
    let changed = false;
    for (const key of Object.keys(state)) {
      if (!active.has(key)) { delete state[key]; changed = true; }
    }
    if (changed) localStorage.setItem(HB_STORAGE_KEY, JSON.stringify(state));
  } catch {}
};

const HiringBoard: React.FC = () => {
  const { t, i18n } = useTranslation('hiringBoard');
  const navigate = useNavigate();
  const { apiCall, user, authReady } = useAuth();
  const { userProfile } = useUserProfile();
  const isRecruiter = !!userProfile?.is_recruiter;

  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [noCompany, setNoCompany] = useState(false);
  const [columns, setColumns] = useState<JobColumn[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [savedCvIds, setSavedCvIds] = useState<Set<string>>(new Set());
  const [reachOutSuggestions, setReachOutSuggestions] = useState<any>(null);
  // Synced ATS openings for this company. When present, they drive the board
  // columns directly (real jobs) instead of AI-parsed titles from looking_for.
  const [atsJobs, setAtsJobs] = useState<{ title?: string; location?: string }[]>([]);
  const [interestedCount, setInterestedCount] = useState(0);

  const [cvModal, setCvModal] = useState<{ cvId: string; images: string[]; imageCount: number } | null>(null);
  const [cvLoading, setCvLoading] = useState(false);
  const [cvPage, setCvPage] = useState(0);

  const [aiResults, setAiResults] = useState<Record<string, { summary: string; fit: string }>>(() => {
    try { return JSON.parse(localStorage.getItem('cevee_hb_ai_cache') || '{}'); } catch { return {}; }
  });
  const [aiLoading, setAiLoading] = useState<Record<string, boolean>>({});

  const [reachOutTarget, setReachOutTarget] = useState<{ candidate: FeedCandidate; jobTitle: string } | null>(null);
  const [reachOutForm, setReachOutForm] = useState({ job_title: '', salary_range: '', message: '' });
  const [reachOutSubmitting, setReachOutSubmitting] = useState(false);
  const [recruiterProfile, setRecruiterProfile] = useState<any>(null);



  // ── Load initial data ──
  useEffect(() => {
    if (!user || !authReady) return;
    (async () => {
      try {
        const resp = await apiCall(`${API_BASE}/api/v1/companies/interested-feed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: '', top_k: 1 }),
        });
        if (!resp.ok) {
          if (resp.status === 429) {
            const apiError = await handleApiError(resp);
            if (apiError.rateLimitInfo) setRateLimitInfo(apiError.rateLimitInfo);
          }
          setNoCompany(true);
          setInitialLoading(false);
          return;
        }
        const data = await resp.json();
        if (data.success) {
          setCompanyName(data.company_name || null);
          setInterestedCount(data.interested_count || 0);
          if (data.reach_out_suggestions) setReachOutSuggestions(data.reach_out_suggestions);
        } else {
          setNoCompany(true);
        }
      } catch { setNoCompany(true); }

      try {
        const resp = await apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs`);
        if (resp.ok) {
          const data = await resp.json();
          const ids = (data.saved_cvs || []).map((s: any) => s.cv_id);
          setSavedCvIds(new Set(ids));
        }
      } catch { /* ignore */ }

      try {
        const resp = await apiCall(`${API_BASE}/api/v1/companies/my-company`);
        if (resp.ok) {
          const data = await resp.json();
          const cid = data.company?.id;
          if (cid) {
            setCompanyId(cid);
            // Prefer real synced ATS openings as the board's job source.
            try {
              const jResp = await apiCall(`${API_BASE}/api/v1/ats/jobs/${cid}`);
              if (jResp.ok) {
                const jData = await jResp.json();
                setAtsJobs(Array.isArray(jData.jobs) ? jData.jobs : []);
              }
            } catch { /* ignore — fall back to looking_for suggestions */ }
          }
        }
      } catch { /* ignore */ }

      try {
        const resp = await apiCall(`${API_BASE}/api/v1/profiles/${user.userId}`);
        if (resp.ok) setRecruiterProfile(await resp.json());
      } catch { /* ignore */ }

      setInitialLoading(false);
    })();
  }, [user?.userId, authReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Generate columns from jobs ──
  // Source priority: real synced ATS openings → otherwise AI-parsed titles
  // from the company's looking_for free text (legacy/manual fallback).
  useEffect(() => {
    const rawSource: { title?: string; location?: string }[] = atsJobs.length > 0
      ? atsJobs
      : (reachOutSuggestions?.job_titles || []);

    // Dedupe by title (ATS can list several openings with the same title);
    // their locations are merged into the shared location filter below.
    const seenTitles = new Set<string>();
    const source = rawSource.filter((jt) => {
      const key = (jt.title || '').trim().toLowerCase();
      if (!key || seenTitles.has(key)) return false;
      seenTitles.add(key);
      return true;
    });
    if (!source.length) return;

    const allLocs: string[] = [];
    const locSeen = new Set<string>();
    for (const jt of rawSource) {
      const raw = jt.location?.trim();
      if (!raw) continue;
      for (const part of raw.split(/[,;\/]+/)) {
        const l = part.trim();
        if (l && !locSeen.has(l.toLowerCase())) { locSeen.add(l.toLowerCase()); allLocs.push(l); }
      }
    }

    const persisted = loadPersistedState();

    const newCols: JobColumn[] = source.map((jt: any, idx: number) => {
      const { role, level } = parseRoleLevel(jt.title || '');
      const saved = persisted[jt.title];
      let activeLocs: string[];
      if (saved?.activeLocations) {
        const savedAllLower = new Set((saved.allLocations || saved.activeLocations).map((l) => l.toLowerCase()));
        const savedActiveLower = new Set(saved.activeLocations.map((l) => l.toLowerCase()));
        activeLocs = allLocs.filter((l) => {
          const low = l.toLowerCase();
          if (savedActiveLower.has(low)) return true;
          if (!savedAllLower.has(low)) return true;
          return false;
        });
      } else {
        activeLocs = [...allLocs];
      }
      return {
        id: 'col_' + Math.random().toString(36).slice(2, 8),
        title: jt.title,
        role: role || jt.title,
        level,
        allLocations: [...allLocs],
        activeLocations: activeLocs,
        customLocations: saved?.customLocations ?? [],
        skills: saved?.skills ?? [],
        languageFilter: saved?.languageFilter ?? null,
        candidates: [],
        loading: false,
        expanded: false,
        loaded: false,
        displayCount: 10,
        totalLoaded: 0,
      };
    });

    setColumns(newCols);
    cleanupPersistedState(newCols.map((c) => c.title));
  }, [atsJobs, reachOutSuggestions]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSeqRef = useRef<Record<string, number>>({});
  const loadColumnCandidates = async (col: JobColumn) => {
    if (!user) return;
    const mySeq = (loadSeqRef.current[col.id] || 0) + 1;
    loadSeqRef.current[col.id] = mySeq;
    const isStale = () => loadSeqRef.current[col.id] !== mySeq;
    const allActiveLocs = [...col.activeLocations, ...col.customLocations];
    const query = [col.title, ...allActiveLocs.slice(0, 4), ...col.skills].filter(Boolean).join(' ');
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/companies/discover-candidates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, top_k: 20 }),
      });
      if (isStale()) return;
      if (!resp.ok) {
        if (resp.status === 429) {
          const apiError = await handleApiError(resp);
          if (apiError.rateLimitInfo) setRateLimitInfo(apiError.rateLimitInfo);
        }
        setColumns((prev) => prev.map((c) => c.id === col.id ? { ...c, loading: false, loaded: true } : c));
        return;
      }
      const data = await resp.json();
      if (isStale()) return;
      if (data.success) {
        setColumns((prev) => prev.map((c) =>
          c.id === col.id
            ? { ...c, candidates: data.candidates || [], loading: false, loaded: true, totalLoaded: (data.candidates || []).length }
            : c
        ));
      } else {
        setColumns((prev) => prev.map((c) =>
          c.id === col.id
            ? { ...c, candidates: [], loading: false, loaded: true, totalLoaded: 0 }
            : c
        ));
      }
    } catch {
      if (isStale()) return;
      setColumns((prev) => prev.map((c) => c.id === col.id ? { ...c, loading: false, loaded: true } : c));
    }
  };
  const loadColumnCandidatesRef = useRef(loadColumnCandidates);
  loadColumnCandidatesRef.current = loadColumnCandidates;

  const openCvModal = async (cvId: string) => {
    setCvLoading(true);
    setCvPage(0);
    setCvModal({ cvId, images: [], imageCount: 0 });
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/cv/${cvId}/images`);
      if (resp.ok) {
        const data = await resp.json();
        setCvModal({ cvId, images: data.images || [], imageCount: data.image_count || 0 });
      }
    } catch { /* ignore */ }
    setCvLoading(false);
  };

  const AI_CACHE_KEY = 'cevee_hb_ai_cache';
  const AI_CACHE_MAX = 300;

  const getAiCache = (): Record<string, { summary: string; fit: string }> => {
    try { return JSON.parse(localStorage.getItem(AI_CACHE_KEY) || '{}'); } catch { return {}; }
  };

  const setAiCache = (key: string, value: { summary: string; fit: string }) => {
    try {
      const cache = getAiCache();
      cache[key] = value;
      const keys = Object.keys(cache);
      if (keys.length > AI_CACHE_MAX) {
        for (const k of keys.slice(0, keys.length - AI_CACHE_MAX)) delete cache[k];
      }
      localStorage.setItem(AI_CACHE_KEY, JSON.stringify(cache));
    } catch {}
  };

  const generateAI = async (candidate: FeedCandidate, jobTitle: string, col: JobColumn) => {
    const cacheKey = `${candidate.cv_id}__${jobTitle}`;
    if (aiResults[cacheKey] || aiLoading[candidate.cv_id]) return;

    const cached = getAiCache()[cacheKey];
    if (cached) {
      setAiResults((p) => ({ ...p, [cacheKey]: cached }));
      return;
    }

    setAiLoading((p) => ({ ...p, [candidate.cv_id]: true }));
    const lang = i18n.language.startsWith('de') ? 'de' : 'en';
    const locContext = col.activeLocations.length > 0 ? ` Locations: ${col.activeLocations.join(', ')}.` : '';
    const skillContext = col.skills.length > 0 ? ` Key skills: ${col.skills.join(', ')}.` : '';
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/spotlights/ai-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cv_id: candidate.cv_id,
          language: lang,
          prompt: `Evaluate this candidate for the role: ${jobTitle}.${locContext}${skillContext} Summarize their background in 2-3 bullet points, then assess fit for this specific role in 2-3 bullet points.`,
        }),
      });
      if (resp.ok) {
        const data = await resp.json();
        const result = { summary: data.summary || '', fit: data.fit || '' };
        setAiResults((p) => ({ ...p, [cacheKey]: result }));
        setAiCache(cacheKey, result);
      } else if (resp.status === 429) {
        const apiError = await handleApiError(resp);
        if (apiError.rateLimitInfo) setRateLimitInfo(apiError.rateLimitInfo);
      }
    } catch { /* ignore */ }
    setAiLoading((p) => ({ ...p, [candidate.cv_id]: false }));
  };

  const [jobSavesVersion, setJobSavesVersion] = useState(0);

  const toggleSave = async (cvId: string, jobTitle: string) => {
    if (!user) return;
    const currentJobSaves = getJobSaves(cvId);
    const isSavedForJob = currentJobSaves.includes(jobTitle);
    const wasGloballySaved = savedCvIds.has(cvId);

    if (isSavedForJob) {
      removeJobSave(cvId, jobTitle);
      setJobSavesVersion((v) => v + 1);
      const remaining = getJobSaves(cvId);
      if (remaining.length === 0 && wasGloballySaved) {
        const next = new Set(savedCvIds);
        next.delete(cvId);
        setSavedCvIds(next);
        try {
          const resp = await apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs/${cvId}`, { method: 'DELETE' });
          if (!resp.ok) { setSavedCvIds(savedCvIds); addJobSave(cvId, jobTitle); setJobSavesVersion((v) => v + 1); }
        } catch { setSavedCvIds(savedCvIds); addJobSave(cvId, jobTitle); setJobSavesVersion((v) => v + 1); }
      }
    } else {
      addJobSave(cvId, jobTitle);
      setJobSavesVersion((v) => v + 1);
      if (!wasGloballySaved) {
        const next = new Set(savedCvIds);
        next.add(cvId);
        setSavedCvIds(next);
        try {
          const resp = await apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cv_id: cvId, prompt: (jobTitle || 'Hiring Board').slice(0, 100) }),
          });
          if (!resp.ok) { setSavedCvIds(savedCvIds); removeJobSave(cvId, jobTitle); setJobSavesVersion((v) => v + 1); }
        } catch { setSavedCvIds(savedCvIds); removeJobSave(cvId, jobTitle); setJobSavesVersion((v) => v + 1); }
      }
    }
  };

  const isProfileIncomplete = recruiterProfile && recruiterProfile.is_recruiter && (
    !recruiterProfile.first_name || !recruiterProfile.last_name || !recruiterProfile.company_name ||
    !recruiterProfile.recruiter_background ||
    !recruiterProfile.consents?.recruiter_profile_visibility?.granted
  );

  const openReachOut = (candidate: FeedCandidate, jobTitle: string) => {
    if (isProfileIncomplete) { navigate('/recruiter-profile'); return; }
    setReachOutTarget({ candidate, jobTitle });
    const lang = i18n.language.startsWith('de') ? 'de' : 'en';
    const intro = reachOutSuggestions?.intro?.[lang] || reachOutSuggestions?.intro?.en || '';
    setReachOutForm({
      job_title: jobTitle,
      salary_range: '',
      message: intro.replace('{{job_title}}', jobTitle),
    });
  };

  const submitReachOut = async () => {
    if (isProfileIncomplete) { navigate('/recruiter-profile'); return; }
    if (!reachOutTarget || !reachOutForm.job_title.trim() || !reachOutForm.salary_range.trim() || !reachOutForm.message.trim()) return;
    setReachOutSubmitting(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/recruiter/reach-out`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cv_id: reachOutTarget.candidate.cv_id,
          job_title: reachOutForm.job_title,
          salary_range: reachOutForm.salary_range,
          message: reachOutForm.message,
        }),
      });
      if (resp.ok) {
        showToast(t('reach_out.sent'), 'success');
        setReachOutTarget(null);
      } else if (resp.status === 429) {
        const apiError = await handleApiError(resp);
        if (apiError.rateLimitInfo) setRateLimitInfo(apiError.rateLimitInfo);
      }
    } catch (err: any) {
      showToast(err.message || t('reach_out.failed'), 'error');
    }
    setReachOutSubmitting(false);
  };

  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const debouncedLoad = useCallback((col: JobColumn) => {
    if (debounceTimers.current[col.id]) clearTimeout(debounceTimers.current[col.id]);
    debounceTimers.current[col.id] = setTimeout(() => loadColumnCandidatesRef.current(col), 300);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleLocation = (colId: string, loc: string) => {
    setColumns((prev) => {
      const updated = prev.map((c) => {
        if (c.id !== colId) return c;
        const isActive = c.activeLocations.includes(loc);
        const next = isActive
          ? c.activeLocations.filter((l) => l !== loc)
          : [...c.activeLocations, loc];
        return { ...c, activeLocations: next, loading: true };
      });
      persistColumns(updated);
      const col = updated.find((c) => c.id === colId);
      if (col) debouncedLoad(col);
      return updated;
    });
  };

  const addCustomLocation = (colId: string, loc: string) => {
    const trimmed = loc.trim();
    if (!trimmed) return;
    setColumns((prev) => {
      const updated = prev.map((c) => {
        if (c.id !== colId) return c;
        const allLower = [...c.allLocations, ...c.customLocations].map((l) => l.toLowerCase());
        if (allLower.includes(trimmed.toLowerCase())) return c;
        return { ...c, customLocations: [...c.customLocations, trimmed], loading: true };
      });
      persistColumns(updated);
      const col = updated.find((c) => c.id === colId);
      if (col && col.loading) debouncedLoad(col);
      return updated;
    });
  };

  const removeCustomLocation = (colId: string, loc: string) => {
    setColumns((prev) => {
      const updated = prev.map((c) => {
        if (c.id !== colId) return c;
        return { ...c, customLocations: c.customLocations.filter((l) => l !== loc), loading: true };
      });
      persistColumns(updated);
      const col = updated.find((c) => c.id === colId);
      if (col) debouncedLoad(col);
      return updated;
    });
  };

  const addSkill = (colId: string, skill: string) => {
    const trimmed = skill.trim();
    if (!trimmed) return;
    setColumns((prev) => {
      const updated = prev.map((c) => {
        if (c.id !== colId) return c;
        if (c.skills.some((s) => s.toLowerCase() === trimmed.toLowerCase())) return c;
        return { ...c, skills: [...c.skills, trimmed], loading: true };
      });
      persistColumns(updated);
      const col = updated.find((c) => c.id === colId);
      if (col && col.loading) debouncedLoad(col);
      return updated;
    });
  };

  const removeSkill = (colId: string, skill: string) => {
    setColumns((prev) => {
      const updated = prev.map((c) => {
        if (c.id !== colId) return c;
        return { ...c, skills: c.skills.filter((s) => s !== skill), loading: true };
      });
      persistColumns(updated);
      const col = updated.find((c) => c.id === colId);
      if (col) debouncedLoad(col);
      return updated;
    });
  };

  const toggleLanguageFilter = (colId: string, lang: string) => {
    setColumns((prev) => {
      const updated = prev.map((c) => {
        if (c.id !== colId) return c;
        return { ...c, languageFilter: c.languageFilter === lang ? null : lang };
      });
      persistColumns(updated);
      return updated;
    });
  };

  const clearAllFilters = (colId: string) => {
    setColumns((prev) => {
      const updated = prev.map((c) => {
        if (c.id !== colId) return c;
        // Locations + skills are server-side filters → reload; language is client-side.
        const hadServerFilters =
          c.activeLocations.length > 0 || c.customLocations.length > 0 || c.skills.length > 0;
        return {
          ...c,
          activeLocations: [],
          customLocations: [],
          skills: [],
          languageFilter: null,
          loading: hadServerFilters,
        };
      });
      persistColumns(updated);
      const col = updated.find((c) => c.id === colId);
      if (col && col.loading) debouncedLoad(col);
      return updated;
    });
  };

  // RCC layout selects an "active" role and lazily loads that role's
  // candidates via the shared loadColumnCandidates path (no new API behavior).
  const selectJobForRcc = useCallback((col: JobColumn) => {
    if (col.loaded || col.loading) return;
    setColumns((prev) => prev.map((c) => (c.id === col.id ? { ...c, loading: true } : c)));
    loadColumnCandidatesRef.current(col);
  }, []);


  if (!user || !authReady) {
    return <RecruiterLoggedOutGate returnTo="/?tab=hire" />;
  }

  if (initialLoading) {
    return <RecruiterLoadingScreen icon={Briefcase} />;
  }

  if (user && !isRecruiter) {
    return (
      <RecruiterPageShell>
        <RecruiterGateCard
          icon={<Briefcase className="size-6 text-white" />}
          title={t('explorer_gate.title')}
          description={t('explorer_gate.description')}
        >
          <ul className="mt-7 space-y-3.5 text-left">
            <FeatureLine icon={<Sparkles className="w-3.5 h-3.5" />}>{t('explorer_gate.feature_sourcing')}</FeatureLine>
            <FeatureLine icon={<ClipboardList className="w-3.5 h-3.5" />}>{t('explorer_gate.feature_invite')}</FeatureLine>
            <FeatureLine icon={<Wand2 className="w-3.5 h-3.5" />}>{t('explorer_gate.feature_formats')}</FeatureLine>
            <FeatureLine icon={<FileText className="w-3.5 h-3.5" />}>{t('explorer_gate.feature_analysis')}</FeatureLine>
          </ul>
          <a
            href={RECRUITER_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 py-3 text-sm font-semibold text-white shadow-lg shadow-pink-500/20 transition-all hover:shadow-xl hover:shadow-pink-500/25"
          >
            {t('explorer_gate.cta')}
          </a>
          <button
            type="button"
            onClick={() => navigate('/?tab=explore')}
            className="mt-3 block w-full text-center text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
          >
            {t('explorer_gate.back')}
          </button>
        </RecruiterGateCard>
      </RecruiterPageShell>
    );
  }

  if (noCompany) {
    return (
      <RecruiterPageShell>
        <RecruiterInlineEmpty
          icon={Building2}
          title={t('no_company.title')}
          description={t('no_company.description')}
          action={
            <Link
              to="/company/manage"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
            >
              <Building2 className="size-4" /> {t('no_company.cta')}
            </Link>
          }
        />
      </RecruiterPageShell>
    );
  }

  return (
    <RecruiterPageShell>

        {/* AI Hiring nudge banner */}
        <RccAiNudgeBanner />

        {/* Rate Limit */}
        {rateLimitInfo && (
          <div className="mb-6">
            <RateLimitNotification rateLimitInfo={rateLimitInfo} feature="candidate_search" onClose={() => setRateLimitInfo(null)} />
          </div>
        )}

        {/* Empty State */}
        {columns.length === 0 && !initialLoading ? (
          <RecruiterInlineEmpty
            icon={Briefcase}
            title={t('empty.title')}
            description={t('empty.description')}
            action={
              <Link
                to="/company/manage"
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
              >
                <Building2 className="size-4" /> {t('empty.cta')}
              </Link>
            }
          />
        ) : (
          <RecruiterCommandCenterBoard
            t={t as any}
            columns={columns}
            companyId={companyId}
            interestedCount={interestedCount}
            aiResults={aiResults}
            aiLoading={aiLoading}
            jobSavesVersion={jobSavesVersion}
            onSelectJob={selectJobForRcc}
            toggleLocation={toggleLocation}
            addCustomLocation={addCustomLocation}
            removeCustomLocation={removeCustomLocation}
            addSkill={addSkill}
            removeSkill={removeSkill}
            toggleLanguageFilter={toggleLanguageFilter}
            clearAllFilters={clearAllFilters}
            openCvModal={openCvModal}
            generateAI={generateAI}
            toggleSave={toggleSave}
            openReachOut={openReachOut}
            openChat={(conversationId: string) => navigate(`/recruiter-inbox?conversation=${conversationId}`)}
            getJobSaves={getJobSaves}
            renderAtsButton={(c) =>
              companyId && c.explorer_id ? (
                <AtsPushButton
                  companyId={companyId}
                  candidateUserId={c.explorer_id}
                  cvId={c.cv_id}
                  t={t as any}
                  appearance="outline"
                  hideIcons
                />
              ) : null
            }
          />
        )}

      {/* ═══ CV Modal ═══ */}
      <AnimatePresence>
        {cvModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4"
            onClick={() => setCvModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-[600px] w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <span className="text-sm font-semibold tracking-tight text-slate-900">{t('card.cv')}</span>
                <button onClick={() => setCvModal(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-auto flex justify-center px-3 pb-3 bg-slate-100 mx-2 mb-2 mt-2 rounded-xl">
                {cvLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-7 h-7 animate-spin text-slate-400" />
                  </div>
                ) : cvModal.images.length > 0 ? (
                  <div className="flex flex-col items-center gap-3 w-full pt-3">
                    <img
                      src={`data:image/png;base64,${cvModal.images[cvPage]}`}
                      alt={t('cv_modal.alt', { page: cvPage + 1 })}
                      className="max-h-[75vh] w-auto object-contain rounded-lg shadow-md"
                    />
                    {cvModal.images.length > 1 && (
                      <div className="flex items-center gap-3 pb-1">
                        <button
                          onClick={() => setCvPage((p) => Math.max(0, p - 1))}
                          disabled={cvPage === 0}
                          className="p-1.5 rounded-lg bg-white hover:bg-slate-200 transition-colors disabled:opacity-30 shadow-sm"
                        >
                          <ChevronLeft className="w-4 h-4 text-slate-600" />
                        </button>
                        <span className="text-sm text-slate-500 font-medium tabular-nums">
                          {cvPage + 1} / {cvModal.images.length}
                        </span>
                        <button
                          onClick={() => setCvPage((p) => Math.min(cvModal!.images.length - 1, p + 1))}
                          disabled={cvPage === cvModal.images.length - 1}
                          className="p-1.5 rounded-lg bg-white hover:bg-slate-200 transition-colors disabled:opacity-30 shadow-sm"
                        >
                          <ChevronRight className="w-4 h-4 text-slate-600" />
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-400 py-20">{t('cv_modal.unavailable')}</p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ Reach-Out Modal ═══ */}
      <AnimatePresence>
        {reachOutTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setReachOutTarget(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              transition={{ type: 'spring', damping: 30, stiffness: 350 }}
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Send className="w-3.5 h-3.5 text-slate-600" />
                  </div>
                  <h3 className="text-sm font-semibold tracking-tight text-slate-900">{t('reach_out.title')}</h3>
                </div>
                <button onClick={() => setReachOutTarget(null)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-2">{t('reach_out.position')}</label>
                  <input
                    value={reachOutForm.job_title}
                    onChange={(e) => setReachOutForm((f) => ({ ...f, job_title: e.target.value }))}
                    className="w-full px-4 py-2.5 text-sm rounded-xl bg-slate-50 border border-slate-200 text-slate-800 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-2">{t('reach_out.salary_range')} <span className="text-red-500">*</span></label>
                  <input
                    value={reachOutForm.salary_range}
                    onChange={(e) => setReachOutForm((f) => ({ ...f, salary_range: e.target.value }))}
                    className="w-full px-4 py-2.5 text-sm rounded-xl bg-slate-50 border border-slate-200 text-slate-800 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200 transition-all"
                    placeholder={t('reach_out.salary_placeholder')}
                    maxLength={100}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-2">{t('reach_out.message')}</label>
                  <textarea
                    value={reachOutForm.message}
                    onChange={(e) => setReachOutForm((f) => ({ ...f, message: e.target.value }))}
                    rows={5}
                    className="w-full px-4 py-2.5 text-sm rounded-xl bg-slate-50 border border-slate-200 text-slate-800 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200 transition-all resize-none"
                  />
                </div>
                <button
                  onClick={submitReachOut}
                  disabled={reachOutSubmitting || !reachOutForm.job_title.trim() || !reachOutForm.salary_range.trim() || !reachOutForm.message.trim()}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {reachOutSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {reachOutSubmitting ? t('reach_out.sending') : t('reach_out.send')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </RecruiterPageShell>
  );
};

export default HiringBoard;
